import React from "react";
import { useMutation } from "@tanstack/react-query";
import {
  Form,
  InputNumber,
  Select,
  Input,
  Typography,
  Row,
  Col,
  Button,
  Alert,
  FormInstance,
} from "antd";
import { useForm } from "antd/es/form/Form";
import {
  DsoConnection,
  SiteReadable as Site,
} from "../../services/client/types.gen";
import { configureSiteMutation } from "../../services/client/@tanstack/react-query.gen";
import { useNotify } from "../util-components/Notifications/notifications";
import {
  DSOConnectionSchema,
  FluviusDSOConnectionSchema,
  AiegDSOConnectionSchema,
  AieshDSOConnectionSchema,
  OresDSOConnectionSchema,
  ResaDSOConnectionSchema,
  RewDSOConnectionSchema,
  SibelgaDSOConnectionSchema,
  GermanDSOConnectionSchema,
  DutchDSOConnectionSchema,
  OtherDutchDSOConnectionSchema,
} from "../../services/client/schemas.gen";
import { useIsViewer } from "../../hooks/useIsViewer";
import { DisableForViewer } from "../common/DisableForViewer";

const { Title } = Typography;

type RenderAs = "input" | "inputNumber" | "select" | "hidden";

type FieldConfig = {
  label?: string;
  renderAs?: RenderAs;
  addonAfter?: string;
  placeholder?: string;
  extra?: string;
  scale?: number; // Divide stored value by this factor for display, multiply on save
};

// ============================================================================
// FIELD CONFIGURATION
// ============================================================================
// Customize field rendering here. Fields not listed will use schema defaults.

const fieldConfig: Record<string, FieldConfig> = {
  region: {
    label: "Region",
    renderAs: "select",
  },
  connection_type: {
    label: "Connection Type",
    renderAs: "select",
  },
  toegangsvermogen: {
    label: "Toegangsvermogen (Access Power)",
    renderAs: "inputNumber",
    addonAfter: "kVA",
    scale: 1000, // Stored in VA, displayed in kVA
  },
  // German (manual-entry) grid tariff fields.
  dso_name: {
    label: "DSO name",
    renderAs: "input",
  },
  metering_type: {
    label: "Metering type",
    renderAs: "select",
  },
  leistungspreis_per_kw_per_year: {
    label: "Leistungspreis",
    renderAs: "inputNumber",
    addonAfter: "€/kW/year",
  },
  arbeitspreis_per_mwh: {
    label: "Grid Arbeitspreis",
    renderAs: "inputNumber",
    addonAfter: "€/MWh",
  },
  grid_fixed_yearly_price: {
    label: "Grid fixed fee",
    renderAs: "inputNumber",
    addonAfter: "€/year",
  },
  stromsteuer_per_mwh: {
    label: "Stromsteuer",
    renderAs: "inputNumber",
    addonAfter: "€/MWh",
  },
  konzessionsabgabe_per_mwh: {
    label: "Konzessionsabgabe",
    renderAs: "inputNumber",
    addonAfter: "€/MWh",
  },
  levies_per_mwh: {
    label: "Levies (§19 + Offshore + KWKG)",
    renderAs: "inputNumber",
    addonAfter: "€/MWh",
  },
  // Dutch grid tariff fields.
  connection_category: {
    label: "Connection category",
    renderAs: "select",
    extra:
      "Kleinverbruik is a connection of up to 3x80A; it has no contracted capacities, so enter 0 for both.",
  },
  contracted_offtake_kw: {
    label: "Contracted offtake capacity",
    renderAs: "inputNumber",
    addonAfter: "kW",
    extra:
      "Gecontracteerd transportvermogen (afname). Offtake is steered at 90% of it so the DSO doesn't raise the contract.",
  },
  contracted_injection_kw: {
    label: "Contracted injection capacity",
    renderAs: "inputNumber",
    addonAfter: "kW",
    extra:
      "Gecontracteerd transportvermogen (teruglevering). Injection is steered at 90% of it so the DSO doesn't raise the contract.",
  },
  expected_yearly_offtake_mwh: {
    label: "Expected yearly offtake",
    renderAs: "inputNumber",
    addonAfter: "MWh/year",
    extra: "Determines the energiebelasting tier.",
  },
  price_per_kw_contracted_per_year: {
    label: "Contracted capacity price",
    renderAs: "inputNumber",
    addonAfter: "€/kW/year",
  },
  price_per_kw_max_per_month: {
    label: "kWmax price",
    renderAs: "inputNumber",
    addonAfter: "€/kW/month",
  },
  grid_energy_per_mwh: {
    label: "Grid energy price",
    renderAs: "inputNumber",
    addonAfter: "€/MWh",
  },
};

// ============================================================================
// SCHEMA TYPES
// ============================================================================

type JsonSchemaProperty = {
  type?: string;
  enum?: string[];
  const?: string;
  title?: string;
  minimum?: number;
};

type JsonSchema = {
  properties?: Record<string, JsonSchemaProperty>;
  required?: string[];
};

// ============================================================================
// DSO OPTIONS
// ============================================================================

type DsoType = DsoConnection["dso"];

const DSO_OPTIONS = Object.keys(
  DSOConnectionSchema.discriminator.mapping,
) as DsoType[];

// Every DSO belongs to exactly one country. The switch is exhaustive, so adding a DSO to the
// client is a type error until it gets a country here. Germany uses a single generic DSO ("German");
// the Netherlands has its large DSOs plus a manual-entry "Other Dutch DSO".
const countryForDso = (dso: DsoType): string => {
  switch (dso) {
    case "Fluvius":
    case "AIEG":
    case "AIESH":
    case "ORES":
    case "RESA":
    case "REW":
    case "Sibelga":
      return "BE";
    case "German":
      return "DE";
    case "Liander":
    case "Stedin":
    case "Enexis":
    case "Other Dutch DSO":
      return "NL";
  }
};

const dsoOptionsForCountry = (country: string): DsoType[] =>
  DSO_OPTIONS.filter((dso) => countryForDso(dso) === country.toUpperCase());

const DSO_SCHEMAS: Record<DsoType, JsonSchema> = {
  Fluvius: FluviusDSOConnectionSchema as unknown as JsonSchema,
  AIEG: AiegDSOConnectionSchema as unknown as JsonSchema,
  AIESH: AieshDSOConnectionSchema as unknown as JsonSchema,
  ORES: OresDSOConnectionSchema as unknown as JsonSchema,
  RESA: ResaDSOConnectionSchema as unknown as JsonSchema,
  REW: RewDSOConnectionSchema as unknown as JsonSchema,
  Sibelga: SibelgaDSOConnectionSchema as unknown as JsonSchema,
  German: GermanDSOConnectionSchema as unknown as JsonSchema,
  Liander: DutchDSOConnectionSchema as unknown as JsonSchema,
  Stedin: DutchDSOConnectionSchema as unknown as JsonSchema,
  Enexis: DutchDSOConnectionSchema as unknown as JsonSchema,
  "Other Dutch DSO": OtherDutchDSOConnectionSchema as unknown as JsonSchema,
};

// Only Enexis publishes a separate tariff for MS connections transported over its network.
const isOptionAvailableForDso = (
  fieldName: string,
  option: string,
  dso: DsoType,
): boolean =>
  fieldName !== "connection_category" ||
  option !== "MS-Transport" ||
  dso === "Enexis";

// ============================================================================
// COMPONENT
// ============================================================================

type DsoConnectionFormProps = {
  site: Site;
  refetchSite: () => void;
};

type FormValues = {
  dso: DsoType | null;
} & Partial<Omit<DsoConnection, "dso">>;

function getFieldsFromSchema(
  schema: JsonSchema,
  fieldConfig: Record<string, FieldConfig>,
): Array<{
  name: string;
  label: string;
  renderAs: RenderAs;
  options?: string[];
  constValue?: string;
  addonAfter?: string;
  placeholder?: string;
  extra?: string;
  minimum?: number;
  required: boolean;
  scale?: number;
}> {
  const properties = schema.properties ?? {};
  const required = schema.required ?? [];

  return Object.entries(properties)
    .filter(([name]) => name !== "dso") // Skip the dso discriminator field
    .map(([name, prop]) => {
      const config = fieldConfig[name] ?? {};
      const label = config.label ?? prop.title ?? name;

      // Determine renderAs
      let renderAs: RenderAs = "input";
      if (prop.const !== undefined) {
        renderAs = "hidden";
      } else if (config.renderAs) {
        renderAs = config.renderAs;
      } else if (prop.enum && prop.enum.length > 0) {
        renderAs = "select";
      } else if (prop.type === "number" || prop.type === "integer") {
        renderAs = "inputNumber";
      }

      return {
        name,
        label,
        renderAs,
        options: prop.enum,
        constValue: prop.const,
        addonAfter: config.addonAfter,
        placeholder: config.placeholder,
        extra: config.extra,
        minimum: prop.minimum,
        required: required.includes(name),
        scale: config.scale,
      };
    });
}

export const DsoConnectionForm: React.FC<DsoConnectionFormProps> = ({
  site,
  refetchSite,
}) => {
  const [form] = useForm<FormValues>();
  const notify = useNotify();
  const isViewer = useIsViewer();
  const selectedDso = Form.useWatch("dso", form);

  const configureSite = useMutation({
    ...configureSiteMutation(),
  });

  const availableDsos = dsoOptionsForCountry(site.country);
  const singleDso = availableDsos.length === 1 ? availableDsos[0] : undefined;

  const currentDsoConfig = site.configurations?.dso;
  const initialValues: FormValues = currentDsoConfig
    ? { ...currentDsoConfig }
    : { dso: singleDso ?? null };

  const onFinish = (values: FormValues) => {
    if (values.dso == null) {
      return;
    }

    // Inject const field values from schema (for hidden fields like region: "All")
    const schema = DSO_SCHEMAS[values.dso];
    if (schema) {
      const fields = getFieldsFromSchema(schema, fieldConfig);
      fields.forEach((field) => {
        if (field.renderAs === "hidden" && field.constValue !== undefined) {
          (values as Record<string, unknown>)[field.name] = field.constValue;
        }
      });
    }

    const dsoConnection = values as DsoConnection;

    configureSite.mutate(
      {
        path: { site_id: site.site_id },
        body: { dso_connection: dsoConnection },
      },
      {
        onError: () => {
          notify({
            type: "error",
            message: "Something went wrong while updating the DSO connection.",
          });
        },
        onSuccess: () => {
          refetchSite();
          notify({
            type: "success",
            message: "DSO connection updated successfully!",
          });
        },
      },
    );
  };

  const clearUnavailableConnectionCategory = (changed: Partial<FormValues>) => {
    // FormValues only knows the fields every DSO shares, and connection_category is Dutch-only.
    const untypedForm: FormInstance = form;
    const category = untypedForm.getFieldValue("connection_category");
    if (
      changed.dso != null &&
      category != null &&
      !isOptionAvailableForDso("connection_category", category, changed.dso)
    ) {
      untypedForm.setFieldValue("connection_category", undefined);
    }
  };

  const renderDsoFields = () => {
    if (selectedDso == null) {
      return null;
    }

    const schema = DSO_SCHEMAS[selectedDso];

    if (!schema) {
      return null;
    }

    const fields = getFieldsFromSchema(schema, fieldConfig);

    return (
      <Row gutter={16}>
        {fields.map((field) => {
          if (field.renderAs === "hidden") {
            return null;
          }

          return (
            <Col span={8} key={field.name}>
              <Form.Item
                name={field.name}
                label={field.label}
                extra={field.extra}
                rules={
                  field.required
                    ? [
                        {
                          required: true,
                          message: `Please enter ${field.label}`,
                        },
                      ]
                    : undefined
                }
                getValueProps={
                  field.scale
                    ? (value) => ({
                        value: value != null ? value / field.scale! : value,
                      })
                    : undefined
                }
                normalize={
                  field.scale
                    ? (value) => (value != null ? value * field.scale! : value)
                    : undefined
                }
              >
                {field.renderAs === "select" && field.options ? (
                  <Select>
                    {field.options
                      .filter((opt) =>
                        isOptionAvailableForDso(field.name, opt, selectedDso),
                      )
                      .map((opt) => (
                        <Select.Option key={opt} value={opt}>
                          {opt}
                        </Select.Option>
                      ))}
                  </Select>
                ) : field.renderAs === "inputNumber" ? (
                  <InputNumber
                    placeholder={field.placeholder}
                    addonAfter={field.addonAfter}
                    min={field.minimum}
                    style={{ width: "100%" }}
                    step={0.01}
                  />
                ) : (
                  <Input
                    placeholder={field.placeholder}
                    addonAfter={field.addonAfter}
                  />
                )}
              </Form.Item>
            </Col>
          );
        })}
      </Row>
    );
  };

  return (
    <div className="mt-8">
      <Title level={3}>DSO Connection</Title>
      <Typography.Paragraph type="secondary" className="mb-4">
        Providing the DSO connection details helps us calculate accurate benefit
        reports and optimize charging schedules based on your grid connection
        prices. If you can't find your DSO, please contact{" "}
        <a href="mailto:support@pleevi.ai">support@pleevi.ai</a>.
      </Typography.Paragraph>
      <Form
        form={form}
        initialValues={initialValues}
        onFinish={onFinish}
        onValuesChange={clearUnavailableConnectionCategory}
        layout="vertical"
        className="bg-gray-50 p-4 rounded-md border-gray-100 border border-solid"
        disabled={isViewer}
      >
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="dso"
              label="Distribution System Operator"
              rules={[{ required: true, message: "Please select a DSO" }]}
            >
              <Select disabled={singleDso != null}>
                {availableDsos.map((dso) => (
                  <Select.Option key={dso} value={dso}>
                    {dso}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {renderDsoFields()}

        <DisableForViewer>
          <Button
            type="primary"
            htmlType="submit"
            loading={configureSite.isPending}
            className="mt-4"
            disabled={selectedDso == null}
          >
            Update DSO Connection
          </Button>
        </DisableForViewer>

        {configureSite.isError && (
          <Alert
            message="Error"
            description="Something went wrong while updating the DSO connection."
            type="error"
            showIcon
            className="mt-4"
          />
        )}
      </Form>
    </div>
  );
};

export default DsoConnectionForm;
